import { useEffect, useRef, useState } from 'react';
import { ChatStream, type ConnectionStatus } from '../lib/ws.js';
import {
  isTwitchMessage,
  isYouTubeMessage,
  isTwitchChatEvent,
  isQuotaWarningEvent,
  type ChatMessage,
  type TwitchChatEvent,
  type ChannelViewerMilestoneEvent,
  type ChannelAnnouncementEvent,
  type ChannelSubGiftRecipientEvent,
  type ChannelSubShareEvent,
} from '../lib/twitch.js';
import { isChannelEvent, type ChannelEvent } from '../lib/events.js';

const MAX_MESSAGES = 500;
const MAX_CHANNEL_EVENTS = 50;

interface ChatStreamState {
  status: ConnectionStatus;
  messages: ChatMessage[];
  /** External-ids of messages that have been deleted (CLEARMSG or username-ban). */
  deletedIds: Set<string>;
  /** Set when a quota_warning event is received from the YouTube ingest. */
  quotaWarning: boolean;
  /** EventSub channel events (follows, subs, cheers, redemptions, raids). */
  channelEvents: ChannelEvent[];
  /**
   * Maps "username\0text" → rewardTitle for channel point redemptions with
   * user input, built from real-time EventSub events + history.
   * Used to annotate matching chat messages with the reward name.
   */
  redemptionMap: Map<string, string>;
  /**
   * The highest `receivedAt` among channelEvents loaded from history (0 if none).
   * `receivedAt` is always server-clock-derived (IRC ingest time or DB created_at),
   * never the client's clock — so comparing against this cutoff to decide whether
   * an event is "new since this page view" (vs. historical) never suffers from
   * client/server clock drift the way comparing against a client-side Date.now()
   * would. Used by ChatList.tsx to decide which events to show inline in chat.
   */
  chatEventsCutoff: number;
}

async function fetchHistory(): Promise<ChatMessage[]> {
  try {
    const res = await fetch('/api/messages/history?limit=200');
    if (!res.ok) return [];
    const json = await res.json() as { messages?: unknown[] };
    if (!Array.isArray(json.messages)) return [];
    return json.messages.filter((m): m is ChatMessage => isTwitchMessage(m) || isYouTubeMessage(m));
  } catch {
    return [];
  }
}

async function fetchEventHistory(): Promise<ChannelEvent[]> {
  try {
    const res = await fetch('/api/events?limit=100');
    if (!res.ok) return [];
    const json = await res.json() as { events?: unknown[] };
    if (!Array.isArray(json.events)) return [];
    return json.events.filter((e): e is ChannelEvent => isChannelEvent(e));
  } catch {
    return [];
  }
}

export function useChatStream(): ChatStreamState {
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(() => new Set<string>());
  const [quotaWarning, setQuotaWarning] = useState(false);
  const [channelEvents, setChannelEvents] = useState<ChannelEvent[]>([]);
  const [redemptionMap, setRedemptionMap] = useState<Map<string, string>>(() => new Map());
  const [chatEventsCutoff, setChatEventsCutoff] = useState(0);
  const bannedUsersRef = useRef<Set<string>>(new Set<string>());

  useEffect(() => {
    fetchHistory().then((history) => {
      if (history.length > 0) {
        setMessages(history);
        // Pre-populate deletedIds from messages that were already deleted in the DB
        // (CLEARCHAT or CLEARMSG during the current stream before this page load).
        const preDeleted = new Set<string>();
        for (const m of history) {
          if (m.isDeleted) preDeleted.add(m.externalId);
        }
        if (preDeleted.size > 0) setDeletedIds(preDeleted);
      }
    });
    fetchEventHistory().then((history) => {
      if (history.length > 0) {
        setChannelEvents(history);
        setChatEventsCutoff(Math.max(...history.map((ev) => ev.receivedAt)));
        // Rebuild redemption map from history
        const map = new Map<string, string>();
        for (const ev of history) {
          if (ev.type === 'redemption' && (ev as { userInput?: string }).userInput) {
            const key = `${ev.username ?? ''}\0${(ev as { userInput: string }).userInput}`;
            map.set(key, (ev as { rewardTitle: string }).rewardTitle);
          }
        }
        if (map.size > 0) setRedemptionMap(map);
      }
    });
  }, []);

  useEffect(() => {
    // Track timeout IDs so we can cancel them on cleanup or on early untimeout/unban.
    const pendingTimeouts: ReturnType<typeof setTimeout>[] = [];
    // Maps username → pending timeout timer (for early cancellation on unban/untimeout).
    const userTimeoutTimers = new Map<string, ReturnType<typeof setTimeout>>();

    const stream = new ChatStream({
      onStatus: (s) => setStatus(s),
      onStreamOnline: () => {
        setMessages([]);
        setDeletedIds(new Set());
        bannedUsersRef.current = new Set();
        setQuotaWarning(false);
        // Events deliberately NOT cleared — they must persist across stream starts
      },
      onChatReset: () => {
        setMessages([]);
        setDeletedIds(new Set());
        bannedUsersRef.current = new Set();
      },
      onMessage: (raw) => {
        let incoming: ChatMessage | null = null;
        if (isTwitchMessage(raw)) {
          incoming = raw;
        } else if (isYouTubeMessage(raw)) {
          incoming = raw;
        }
        if (!incoming) return;

        const msg = incoming;
        // If the user is currently banned, mark this message as deleted on arrival.
        if (bannedUsersRef.current.has(msg.username.toLowerCase())) {
          setDeletedIds((prev) => {
            const next = new Set(prev);
            next.add(msg.externalId);
            return next;
          });
        }
        setMessages((prev) => {
          // Deduplicate: skip if already in history
          if (prev.some((m) => m.externalId === msg.externalId)) return prev;
          if (prev.length >= MAX_MESSAGES) {
            return [...prev.slice(prev.length - MAX_MESSAGES + 1), msg];
          }
          return [...prev, msg];
        });
      },
      onChatEvent: (raw) => {
        // Quota warning from YouTube ingest
        if (isQuotaWarningEvent(raw)) {
          setQuotaWarning(true);
          return;
        }

        if (!isTwitchChatEvent(raw)) return;
        const ev: TwitchChatEvent = raw;

        if (ev.type === 'delete' && ev.targetMessageId) {
          const id = ev.targetMessageId;
          setDeletedIds((prev) => {
            const next = new Set(prev);
            next.add(id);
            return next;
          });
          return;
        }

        if (ev.type === 'clearchat' && ev.targetUsername) {
          const user = ev.targetUsername.toLowerCase();
          bannedUsersRef.current.add(user);
          setMessages((prevMessages) => {
            setDeletedIds((prevDeleted) => {
              const next = new Set(prevDeleted);
              for (const m of prevMessages) {
                if (m.username.toLowerCase() === user) next.add(m.externalId);
              }
              return next;
            });
            return prevMessages;
          });
          // Timeout (not permanent ban): remove from bannedRef after duration so new
          // messages from that user are no longer auto-deleted once they can chat again.
          if (ev.banDurationSeconds != null && ev.banDurationSeconds > 0) {
            // Cancel any existing timer for this user (e.g. timeout extended)
            const existing = userTimeoutTimers.get(user);
            if (existing !== undefined) {
              clearTimeout(existing);
              pendingTimeouts.splice(pendingTimeouts.indexOf(existing), 1);
            }
            const tid = setTimeout(() => {
              bannedUsersRef.current.delete(user);
              userTimeoutTimers.delete(user);
            }, ev.banDurationSeconds * 1_000 + 2_000); // +2s buffer for IRC delivery delay
            pendingTimeouts.push(tid);
            userTimeoutTimers.set(user, tid);
          }
        }

        // Unban / untimeout — via chat tool API (type:'unban') OR via native Twitch
        // IRC NOTICE (type:'notice', noticeType:'untimeout'|'unban').
        // In both cases: remove user from banned list so new messages appear normally.
        const isUnbanEvent = ev.type === 'unban' && ev.targetUsername;
        const isUnbanNotice = ev.type === 'notice' &&
          (ev.noticeType === 'untimeout' || ev.noticeType === 'unban') &&
          ev.username;
        if (isUnbanEvent || isUnbanNotice) {
          const rawUser = isUnbanEvent ? ev.targetUsername! : ev.username!;
          const user = rawUser.toLowerCase();
          bannedUsersRef.current.delete(user);
          // Cancel the pending auto-expire timer if one exists
          const existing = userTimeoutTimers.get(user);
          if (existing !== undefined) {
            clearTimeout(existing);
            userTimeoutTimers.delete(user);
            const idx = pendingTimeouts.indexOf(existing);
            if (idx !== -1) pendingTimeouts.splice(idx, 1);
          }
          // Restore previously deleted messages from this user in real-time.
          setMessages((prevMessages) => {
            setDeletedIds((prevDeleted) => {
              const next = new Set(prevDeleted);
              for (const m of prevMessages) {
                if (m.username.toLowerCase() === user) next.delete(m.externalId);
              }
              return next;
            });
            return prevMessages;
          });
          return;
        }

        if (ev.type === 'notice' && ev.noticeType === 'viewermilestone') {
          const payload = ev.payload as Record<string, string> | undefined;
          const category = payload?.['msg-param-category'] ?? '';
          if (category === 'watch-streak') {
            const milestone: ChannelViewerMilestoneEvent = {
              type: 'viewer_milestone',
              source: 'twitch',
              username: ev.username ?? '',
              displayName: ev.username ?? '',
              streakCount: Number(payload?.['msg-param-value'] ?? 0),
              systemMsg: ev.systemMsg ?? '',
              message: payload?.['_trailing'] ?? '',
              receivedAt: ev.receivedAt,
            };
            setChannelEvents((prev) => {
              const next = [...prev, milestone];
              return next.length > MAX_CHANNEL_EVENTS ? next.slice(next.length - MAX_CHANNEL_EVENTS) : next;
            });
          }
        }

        // First-time subscriber's shared message (msg-id=sub). Only emitted when
        // a message is actually present — the sub itself is already shown via
        // the aggregate EventSub ChannelSubscribeEvent (onChannelEvent below).
        if (ev.type === 'notice' && ev.noticeType === 'sub') {
          const payload = ev.payload as Record<string, string> | undefined;
          const message = payload?.['_trailing'] ?? '';
          if (message) {
            const subShare: ChannelSubShareEvent = {
              type: 'sub_share_message',
              source: 'twitch',
              username: ev.username ?? '',
              displayName: payload?.['display-name'] ?? ev.username ?? '',
              message,
              receivedAt: ev.receivedAt,
            };
            setChannelEvents((prev) => {
              const next = [...prev, subShare];
              return next.length > MAX_CHANNEL_EVENTS ? next.slice(next.length - MAX_CHANNEL_EVENTS) : next;
            });
          }
        }

        if (ev.type === 'notice' && ev.noticeType === 'announcement') {
          const payload = ev.payload as Record<string, string> | undefined;
          const announcement: ChannelAnnouncementEvent = {
            type: 'announcement',
            source: 'twitch',
            username: ev.username ?? '',
            displayName: payload?.['display-name'] ?? ev.username ?? '',
            message: payload?.['_trailing'] ?? '',
            color: (payload?.['msg-param-color'] ?? '').toLowerCase(),
            receivedAt: ev.receivedAt,
          };
          setChannelEvents((prev) => {
            const next = [...prev, announcement];
            return next.length > MAX_CHANNEL_EVENTS ? next.slice(next.length - MAX_CHANNEL_EVENTS) : next;
          });
        }

        if (ev.type === 'notice' && ev.noticeType === 'subgift') {
          const payload = ev.payload as Record<string, string> | undefined;
          const isAnonymous = (ev.username ?? '').toLowerCase() === 'ananonymousgifter';
          const giftRecipient: ChannelSubGiftRecipientEvent = {
            type: 'gift_sub_recipient',
            source: 'twitch',
            gifterUsername: isAnonymous ? null : (ev.username ?? null),
            gifterDisplayName: isAnonymous ? 'Anonym' : (payload?.['display-name'] ?? ev.username ?? ''),
            recipientDisplayName:
              payload?.['msg-param-recipient-display-name'] ?? payload?.['msg-param-recipient-user-name'] ?? '',
            tier: payload?.['msg-param-sub-plan'] ?? '1000',
            isAnonymous,
            receivedAt: ev.receivedAt,
          };
          setChannelEvents((prev) => {
            const next = [...prev, giftRecipient];
            return next.length > MAX_CHANNEL_EVENTS ? next.slice(next.length - MAX_CHANNEL_EVENTS) : next;
          });
        }
      },
      onChannelEvent: (raw) => {
        if (!isChannelEvent(raw)) return;
        const ev = raw;
        setChannelEvents((prev) => {
          // Twitch fires both channel.subscribe AND channel.subscription.message for resubscriptions.
          // When a resub arrives, drop any recent 'subscribe' event from the same user (dedup window: 5s).
          let base = prev;
          if (ev.type === 'resub') {
            base = prev.filter(
              (e) => !(e.type === 'subscribe' && e.username === ev.username && ev.receivedAt - e.receivedAt < 5_000)
            );
          }
          const next = [...base, ev];
          return next.length > MAX_CHANNEL_EVENTS ? next.slice(next.length - MAX_CHANNEL_EVENTS) : next;
        });
        // Track redemptions with user input so chat messages can be annotated (Twitch only)
        if (ev.type === 'redemption') {
          const r = ev as { username?: string; userInput?: string; rewardTitle?: string };
          if (r.userInput && r.rewardTitle) {
            const key = `${r.username ?? ''}\0${r.userInput}`;
            setRedemptionMap((prev) => {
              const next = new Map(prev);
              next.set(key, r.rewardTitle!);
              return next;
            });
          }
        }
      },
    });
    stream.connect();
    return () => {
      stream.disconnect();
      for (const tid of pendingTimeouts) clearTimeout(tid);
    };
  }, []);

  return { status, messages, deletedIds, quotaWarning, channelEvents, redemptionMap, chatEventsCutoff };
}
